/**
 * Addresses reachable only from inside a private network the operator already controls.
 *
 * This is the third rendering of one rule and the first that other TypeScript can import. The Node
 * build gate (`scripts/desktop-updater-endpoint.mjs`) and the Rust runtime gate
 * (`desktop/src-tauri/src/desktop_updates.rs`) each carry their own, because neither can call into
 * this package — one is plain ESM run before any build, the other is Rust. They do not drift
 * because all three are pinned to `fixtures/updater-endpoint-cases.v1.json` (ADR-042), which is the
 * only reason a third copy is allowable at all: the copies are renderings of a shared table, not
 * independent statements of the rule.
 *
 * Anything in the web or shared packages must import this one rather than write a fourth.
 *
 * The tailnet range is the case that matters. Evoye's dev deployment is served over Tailscale, a
 * WireGuard mesh where every peer is authenticated by key and every packet is encrypted before it
 * reaches the wire. That is the property TLS would otherwise provide, and it is provided whether or
 * not the URL says https.
 */
const PRIVATE_IPV4_RANGES: ReadonlyArray<(octets: readonly number[]) => boolean> = [
  (octets) => octets[0] === 127,
  (octets) => octets[0] === 10,
  (octets) => octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31,
  (octets) => octets[0] === 192 && octets[1] === 168,
  // 100.64.0.0/10 — the CGNAT range Tailscale assigns.
  (octets) => octets[0] === 100 && octets[1] >= 64 && octets[1] <= 127,
  (octets) => octets[0] === 169 && octets[1] === 254,
];

export function isPrivateHost(hostname: string | null | undefined): boolean {
  const host = (hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  if (!host) return false;
  if (host === 'localhost' || host === '::1' || host.endsWith('.localhost')) return true;
  // Tailscale MagicDNS names resolve only inside the tailnet that issued them.
  if (host === 'ts.net' || host.endsWith('.ts.net')) return true;
  if (host.startsWith('fe80:') || host.startsWith('fc') || host.startsWith('fd')) return true;

  const parts = host.split('.');
  if (parts.length !== 4) return false;
  const octets = parts.map((part) => (/^\d{1,3}$/.test(part) ? Number(part) : Number.NaN));
  if (octets.some((octet) => Number.isNaN(octet) || octet > 255)) return false;
  return PRIVATE_IPV4_RANGES.some((matches) => matches(octets));
}

/**
 * The scheme a bare address should be completed to.
 *
 * `https` everywhere except a private host, which gets `http`. The asymmetry is the same trade
 * ADR-042 made for the updater endpoint and is made here for the same reason: a deployment on the
 * public internet carrying credentials over plain http is a mistake nobody types deliberately,
 * while a tailnet address cannot obtain a certificate at all — `tailscale cert` on this tailnet
 * answers that the account does not support TLS certs, and no public CA will certify a
 * 100.64.0.0/10 address. Defaulting those to https makes the dev deployment unreachable and, worse,
 * reports it as unreachable under a name it was never contacted by (I-0236's neighbour: the address
 * shown was the one typed, not the one dialled).
 */
export function defaultSchemeForHost(hostname: string | null | undefined): 'http:' | 'https:' {
  return isPrivateHost(hostname) ? 'http:' : 'https:';
}
